import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { on } from '@ember/modifier';
import { fn } from '@ember/helper';
import { modifier } from 'ember-modifier';
import Button from 'tio-ui/components/button';
import Checkbox from 'tio-ui/components/checkbox';
import SearchInput from 'tio-ui/components/search-input';
import Chip from 'tio-ui/components/chip';
import { PhCaretDown, PhCaretRight } from 'ember-phosphor-icons';
import { type FieldOption } from '../utils/opensearch-field-mapper';

/**
 * Modifier to set the indeterminate property on a checkbox input element.
 * This is needed because `indeterminate` is a DOM property, not an HTML attribute.
 */
const setIndeterminate = modifier((element: Element, [value]: [boolean]) => {
  if (element instanceof HTMLInputElement) {
    element.indeterminate = value;
    return;
  }

  const input = element.querySelector('input[type="checkbox"]');
  if (input instanceof HTMLInputElement) {
    input.indeterminate = value;
  }
});

// ---------------------------------------------------------------------------
// ColumnSelector – extracted column selector drawer content
// ---------------------------------------------------------------------------

interface ColumnSelectorSignature {
  Args: {
    /** Grouped fields as [groupName, fields] tuples, already sorted */
    groupedFields: Array<[string, FieldOption[]]>;
    /** Check if a field key is currently selected */
    isColumnSelected: (fieldKey: string) => boolean;
    /** Toggle a single column on/off */
    handleColumnToggle: (fieldKey: string) => void;
    /**
     * Optional bulk group toggle. When provided, used instead of calling
     * handleColumnToggle in a loop (which can be interrupted by confirmation
     * modals, leaving a group partially toggled).
     */
    handleGroupToggle?: (fieldKeys: string[], selectAll: boolean) => void;
    /** Select all columns globally */
    selectAllColumns: () => void;
    /** Deselect all columns globally */
    deselectAllColumns: () => void;
    /** Whether the field mapping is still loading */
    isLoading: boolean;
    /** Total number of available fields */
    totalFieldCount: number;
    /** Number of currently selected columns */
    selectedCount: number;
  };
  Element: HTMLDivElement;
}

export default class ColumnSelector extends Component<ColumnSelectorSignature> {
  @tracked searchQuery = '';

  /**
   * Tracks explicit user overrides to group collapse state.
   * `null` means the user hasn't toggled anything yet — the default
   * collapsed set is derived from args in `effectiveCollapsedGroups`.
   */
  @tracked _userCollapsedGroups: Set<string> | null = null;

  // -----------------------------------------------------------------------
  // Search
  // -----------------------------------------------------------------------

  handleSearchChange = (value: string) => {
    this.searchQuery = value;
  };

  handleSearchClear = () => {
    this.searchQuery = '';
  };

  /**
   * Filter grouped fields by search query, returning only groups/fields that match.
   * When no search query is active, returns all groups.
   */
  get filteredGroupedFields(): Array<[string, FieldOption[]]> {
    const query = this.searchQuery.toLowerCase().trim();
    if (!query) {
      return this.args.groupedFields;
    }

    const filtered: Array<[string, FieldOption[]]> = [];
    for (const [groupName, fields] of this.args.groupedFields) {
      // Match group name or individual field labels
      const matchingFields = fields.filter(
        (field) =>
          field.label.toLowerCase().includes(query) ||
          field.key.toLowerCase().includes(query) ||
          groupName.toLowerCase().includes(query)
      );
      if (matchingFields.length > 0) {
        filtered.push([groupName, matchingFields]);
      }
    }
    return filtered;
  }

  // -----------------------------------------------------------------------
  // Collapse / Expand
  // -----------------------------------------------------------------------

  /**
   * Compute the effective collapsed groups. If the user has explicitly toggled
   * any group, use their set. Otherwise, derive defaults from args: collapse
   * groups with no selected columns. This is a pure getter — no tracked-state
   * mutation — so it's safe during rendering.
   */
  get effectiveCollapsedGroups(): Set<string> {
    if (this._userCollapsedGroups !== null) {
      return this._userCollapsedGroups;
    }
    // Default: collapse groups that have NO selected columns
    const collapsed = new Set<string>();
    for (const [groupName, fields] of this.args.groupedFields) {
      const hasSelected = fields.some((f) => this.args.isColumnSelected(f.key));
      if (!hasSelected) {
        collapsed.add(groupName);
      }
    }
    return collapsed;
  }

  isGroupCollapsed = (groupName: string): boolean => {
    // When searching, always expand all groups to show results
    if (this.searchQuery.trim()) {
      return false;
    }
    return this.effectiveCollapsedGroups.has(groupName);
  };

  toggleGroupCollapse = (groupName: string) => {
    // During search all groups are forced-expanded; ignore collapse toggles
    if (this.searchQuery.trim()) {
      return;
    }
    // Materialise user overrides from the current effective state
    const updated = new Set(this.effectiveCollapsedGroups);
    if (updated.has(groupName)) {
      updated.delete(groupName);
    } else {
      updated.add(groupName);
    }
    this._userCollapsedGroups = updated;
  };

  // -----------------------------------------------------------------------
  // Group-level select / deselect
  // -----------------------------------------------------------------------

  getGroupSelectedCount = (fields: FieldOption[]): number => {
    return fields.filter((f) => this.args.isColumnSelected(f.key)).length;
  };

  isGroupAllSelected = (fields: FieldOption[]): boolean => {
    return fields.length > 0 && fields.every((f) => this.args.isColumnSelected(f.key));
  };

  isGroupIndeterminate = (fields: FieldOption[]): boolean => {
    const selectedCount = this.getGroupSelectedCount(fields);
    return selectedCount > 0 && selectedCount < fields.length;
  };

  toggleGroupSelectAll = (fields: FieldOption[]) => {
    const allSelected = this.isGroupAllSelected(fields);
    const selectAll = !allSelected;

    // Collect the keys that actually need toggling
    const keysToToggle = fields
      .filter((f) => this.args.isColumnSelected(f.key) !== selectAll)
      .map((f) => f.key);

    if (keysToToggle.length === 0) return;

    // Prefer bulk callback when available – calling handleColumnToggle in a
    // loop can be interrupted by confirmation modals (e.g. "required by active
    // filters"), leaving a group partially toggled.
    if (this.args.handleGroupToggle) {
      this.args.handleGroupToggle(keysToToggle, selectAll);
    } else {
      for (const key of keysToToggle) {
        this.args.handleColumnToggle(key);
      }
    }
  };

  // -----------------------------------------------------------------------
  // Capability badges
  // -----------------------------------------------------------------------

  hasCapabilities = (field: FieldOption): boolean => {
    return !!(
      field.isFilterable ||
      field.isSortable ||
      field.isGroupable ||
      field.isRangeQueryable
    );
  };

  // -----------------------------------------------------------------------
  // Template
  // -----------------------------------------------------------------------

  <template>
    <div class="tio-col-loose" ...attributes>
      {{! Summary bar }}
      <div class="tio-row-between">
        <span class="text-sm tio-fg-muted">
          <span class="font-semibold tio-fg-primary">{{@selectedCount}}</span>
          of
          <span class="font-semibold tio-fg-primary">{{@totalFieldCount}}</span>
          columns selected
        </span>
        <div class="tio-flex-tight">
          <Button
            @intent="primary"
            @appearance="outlined"
            @size="sm"
            {{on "click" @selectAllColumns}}
          >
            Select All
          </Button>
          <Button
            @intent="default"
            @appearance="outlined"
            @size="sm"
            {{on "click" @deselectAllColumns}}
          >
            Deselect All
          </Button>
        </div>
      </div>

      {{! Search input }}
      <SearchInput
        @value={{this.searchQuery}}
        @onChange={{this.handleSearchChange}}
        @onClear={{this.handleSearchClear}}
        @placeholder="Search columns..."
        @size="sm"
        @debounce={{150}}
      />

      {{! Loading state }}
      {{#if @isLoading}}
        <div class="p-4 text-center tio-fg-muted">
          <p class="text-sm">Loading available columns...</p>
        </div>
      {{else}}
        {{! No results }}
        {{#if (this.noResults this.filteredGroupedFields)}}
          <div class="p-6 text-center tio-fg-disabled">
            <p class="text-sm">No columns match "{{this.searchQuery}}"</p>
          </div>
        {{else}}
          {{! Grouped field sections }}
          <div class="space-y-1">
            {{#each this.filteredGroupedFields as |groupData|}}
              {{#let
                (this.getGroupName groupData) (this.getGroupFields groupData)
                as |groupName fields|
              }}
                <div class="border tio-border-subtle rounded-lg overflow-hidden">
                  {{! Group header row }}
                  <div class="tio-row-tight px-3 py-2.5 tio-bg-sunken">
                    {{! Collapse toggle }}
                    <button
                      type="button"
                      aria-label="Toggle {{groupName}} section"
                      class="tio-fg-disabled shrink-0 hover:tio-fg-muted transition-colors p-0.5 -ml-1"
                      {{on "click" (fn this.toggleGroupCollapse groupName)}}
                    >
                      {{#if (this.isGroupCollapsed groupName)}}
                        <PhCaretRight @size={{14}} />
                      {{else}}
                        <PhCaretDown @size={{14}} />
                      {{/if}}
                    </button>
                    <h4 class="flex-1 tio-text-sm-semibold tio-fg-secondary m-0">
                      {{groupName}}
                    </h4>

                    {{! Group-level checkbox }}
                    <span class="shrink-0">
                      <Checkbox
                        @checked={{this.isGroupAllSelected fields}}
                        @isIndeterminate={{this.isGroupIndeterminate fields}}
                        @onChange={{fn this.toggleGroupSelectAll fields}}
                        aria-label="Toggle all {{groupName}} columns"
                        {{setIndeterminate (this.isGroupIndeterminate fields)}}
                      />
                    </span>

                    {{! Selected count }}
                    <span class="text-xs tio-fg-muted shrink-0 min-w-[4rem] text-right">
                      {{this.getGroupSelectedCount fields}}
                      of
                      {{fields.length}}
                    </span>
                  </div>

                  {{! Field list (collapsible) }}
                  {{#unless (this.isGroupCollapsed groupName)}}
                    <div class="divide-y tio-divide-subtle">
                      {{#each fields as |field|}}
                        <div class="tio-row-tight px-3 py-2 hover:tio-bg-sunken transition-colors">
                          <Checkbox
                            @checked={{@isColumnSelected field.key}}
                            @onChange={{fn @handleColumnToggle field.key}}
                            aria-label={{field.label}}
                          />
                          <span class="flex-1 text-sm tio-fg-primary">{{field.label}}</span>
                          {{! Capability badges }}
                          {{#if (this.hasCapabilities field)}}
                            <span class="tio-flex-extra-tight shrink-0">
                              {{#if field.isFilterable}}
                                <Chip
                                  @size="sm"
                                  @intent="primary"
                                  @appearance="faded"
                                  title="Filterable"
                                  aria-label="Filterable"
                                >F</Chip>
                              {{/if}}
                              {{#if field.isSortable}}
                                <Chip
                                  @size="sm"
                                  @intent="success"
                                  @appearance="faded"
                                  title="Sortable"
                                  aria-label="Sortable"
                                >S</Chip>
                              {{/if}}
                              {{#if field.isGroupable}}
                                <Chip
                                  @size="sm"
                                  @intent="warning"
                                  @appearance="faded"
                                  title="Groupable"
                                  aria-label="Groupable"
                                >G</Chip>
                              {{/if}}
                              {{#if field.isRangeQueryable}}
                                <Chip
                                  @size="sm"
                                  @intent="default"
                                  @appearance="faded"
                                  title="Range queryable"
                                  aria-label="Range queryable"
                                >R</Chip>
                              {{/if}}
                            </span>
                          {{/if}}
                        </div>
                      {{/each}}
                    </div>
                  {{/unless}}
                </div>
              {{/let}}
            {{/each}}
          </div>
        {{/if}}
      {{/if}}
    </div>
  </template>

  // -----------------------------------------------------------------------
  // Template helpers (arrow functions for use in templates)
  // -----------------------------------------------------------------------

  noResults = (groups: Array<[string, FieldOption[]]>): boolean => {
    return groups.length === 0 && this.searchQuery.trim().length > 0;
  };

  getGroupName = (groupData: [string, FieldOption[]]): string => {
    return groupData[0];
  };

  getGroupFields = (groupData: [string, FieldOption[]]): FieldOption[] => {
    return groupData[1];
  };
}
